import * as Icons from "@nova/component-icons"
import { ButtonTertiary, ButtonSecondary, ButtonStatus } from "@nova/component-btn"
import { describeRelativeTime } from "@nova/format"
import { IntegrationType } from "@nova/schemas"
import { useMemo } from "react"
import { usePilotTrackingConnectionsContext } from "features/data-management/pilot-tracking/PilotTrackingConnectionsContext"
import { useIsEditor } from "tools/hooks/useIsEditor"
import { useAppDispatch } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useRouter } from "pages"

const INTEGRATION_LABELS: Partial<Record<IntegrationType, string>> = {
    [IntegrationType.dji]: "DJI",
    [IntegrationType.autel]: "Autel",
    [IntegrationType.skydio]: "Skydio",
}

type ProviderSync = { label: string; lastSyncedAt: Date }

export function SyncAllAccountsFooter() {
    const isEditor = useIsEditor()
    const dispatch = useAppDispatch()
    const router = useRouter("/workspace/:workspaceId")

    const {
        accountsByIntegrationType,
        hasConnectedAccount,
        syncStatuses,
        isLoading,
        triggerDjiSync,
        triggerAutelSync,
        triggerSkydioSync,
    } = usePilotTrackingConnectionsContext()

    const triggerByType = useMemo(
        (): Partial<Record<IntegrationType, (id: string) => Promise<void>>> => ({
            [IntegrationType.dji]: triggerDjiSync,
            [IntegrationType.autel]: triggerAutelSync,
            [IntegrationType.skydio]: triggerSkydioSync,
        }),
        [triggerDjiSync, triggerAutelSync, triggerSkydioSync]
    )

    const accounts = useMemo(
        () => Object.values(accountsByIntegrationType).flat(),
        [accountsByIntegrationType]
    )

    const isSyncing = accounts.some((a) => syncStatuses[a.id]?.status === "syncing")

    const progress = useMemo(() => {
        let synced = 0
        let total = 0
        accounts.forEach((a) => {
            const status = syncStatuses[a.id]
            if (status?.status !== "syncing" || status.filesTotal == null) return
            synced += status.filesSynced
            total += status.filesTotal
        })
        return { synced, total }
    }, [accounts, syncStatuses])

    const lastSyncedAt = useMemo(() => {
        const times = accounts
            .map((a) => syncStatuses[a.id]?.triggeredAt)
            .filter((t): t is Date => !!t)
        if (!times.length) return undefined
        return times.reduce((min, t) => (t < min ? t : min))
    }, [accounts, syncStatuses])

    const providerSyncs = useMemo<ProviderSync[]>(() => {
        return (Object.keys(accountsByIntegrationType) as IntegrationType[])
            .map((type) => {
                const label = INTEGRATION_LABELS[type]
                if (!label) return undefined
                const times = (accountsByIntegrationType[type] ?? [])
                    .map((a) => syncStatuses[a.id]?.triggeredAt)
                    .filter((t): t is Date => !!t)
                if (!times.length) return undefined
                const min = times.reduce((m, t) => (t < m ? t : m))
                return { label, lastSyncedAt: min }
            })
            .filter((p): p is ProviderSync => !!p)
    }, [accountsByIntegrationType, syncStatuses])

    if (isLoading) return null

    if (!hasConnectedAccount) {
        if (!isEditor) return null
        return (
            <div className="border-t border-t-gray-700 p-3">
                <ButtonSecondary
                    title="Connect an Account"
                    RightIcon={Icons.ArrowRight}
                    onClick={() =>
                        router.navigate(
                            { path: "/settings/connected-accounts" },
                            { state: "openSelectProvider" }
                        )
                    }
                    className="w-full"
                />
            </div>
        )
    }

    const syncAll = async () => {
        try {
            await Promise.all(accounts.map((a) => triggerByType[a.integrationType]?.(a.id)))
        } catch (err) {
            dispatch(
                setGlobalNotificationContext({
                    message: err instanceof Error ? err.message : "Failed to start sync",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
    }

    if (isSyncing) {
        return (
            <div className="flex flex-col gap-2 border-t border-t-gray-700 p-3">
                <div className="flex items-center gap-1 text-xs text-gray-400 whitespace-nowrap">
                    <span>Syncing:</span>
                    <span className="text-gray-200">
                        {progress.synced}/{progress.total} flights
                    </span>
                </div>
                <ButtonSecondary
                    title="Syncing..."
                    status={ButtonStatus.Loading}
                    className="w-full"
                />
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-2 border-t border-t-gray-700 p-3">
            <div className="flex items-center justify-between text-xs text-gray-400 whitespace-nowrap">
                <div className="flex items-center gap-1">
                    <span>Last synced:</span>
                    <LastSyncedTooltip providerSyncs={providerSyncs} />
                </div>
                <span>{lastSyncedAt ? describeRelativeTime(lastSyncedAt) : "Never"}</span>
            </div>
            <ButtonTertiary
                title="Sync All Accounts"
                LeftIcon={Icons.Sync}
                onClick={syncAll}
                disabled={!isEditor}
                className="w-full"
            />
        </div>
    )
}

function LastSyncedTooltip({ providerSyncs }: { providerSyncs: ProviderSync[] }) {
    if (!providerSyncs.length) return null
    return (
        <div className="relative flex items-center group/synctip">
            <Icons.SmallIcon Icon={Icons.Information} className="text-gray-400" />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/synctip:block z-[60]">
                <div className="bg-gray-900 border border-gray-700 rounded-lg px-2 py-1 shadow-lg">
                    {providerSyncs.map((p) => (
                        <div
                            key={p.label}
                            className="flex items-center justify-between gap-4 text-xs text-gray-50 whitespace-nowrap"
                        >
                            <span>{p.label}</span>
                            <span>{describeRelativeTime(p.lastSyncedAt)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
